import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const fmt = n => `$${(n||0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
const hoy = new Date().toISOString().split('T')[0];

const TIPOS_IMPUESTO = [
  'Monotributo / AFIP',
  'Ingresos Brutos',
  'Tasa Municipal',
  'Seguridad e Higiene',
  'Contribuciones Patronales',
  'Obra Social',
  'ART',
  'Alquiler',
  'Otro',
];

function getEstado(imp) {
  if ((imp.pagado || 0) >= imp.monto) return 'pagado';
  if (new Date(imp.fecha_vencimiento) < new Date()) return 'vencido';
  return 'pendiente';
}

const ESTADO_STYLE = {
  pagado:   { label:'✅ Pagado',   bg:'#D5F5E3', color:'#1E8449' },
  vencido:  { label:'🔴 Vencido',  bg:'#FADBD8', color:'#C0392B' },
  pendiente:{ label:'⏳ Pendiente', bg:'#FEF9E7', color:'#D4AC0D' },
};

export default function Impuestos() {
  const navigate = useNavigate();
  const [impuestos, setImpuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('lista');
  const [exito, setExito] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    nombre: '', periodo: '', fecha_vencimiento: '', monto: '', pagado: 0, observaciones: ''
  });

  useEffect(() => { cargarDatos(); }, []);

  async function cargarDatos() {
    setLoading(true);
    const { data } = await supabase
      .from('impuestos')
      .select('*')
      .order('fecha_vencimiento', { ascending: true });
    setImpuestos(data || []);
    setLoading(false);
  }

  async function guardarImpuesto() {
    if (!form.nombre || !form.fecha_vencimiento || !form.monto) {
      alert('Completá nombre, fecha de vencimiento y monto');
      return;
    }
    setGuardando(true);
    const { error } = await supabase.from('impuestos').insert({
      nombre: form.nombre,
      periodo: form.periodo,
      fecha_vencimiento: form.fecha_vencimiento,
      monto: parseFloat(form.monto),
      pagado: parseFloat(form.pagado) || 0,
      observaciones: form.observaciones,
    });
    setGuardando(false);
    if (error) { alert('Error: ' + error.message); return; }
    mostrarExito('¡Impuesto registrado correctamente!');
    setForm({ nombre:'', periodo:'', fecha_vencimiento:'', monto:'', pagado:0, observaciones:'' });
    cargarDatos();
    setTab('lista');
  }

  async function registrarPago(id, pagadoActual, monto) {
    const valor = prompt(`¿Cuánto pagás? (Saldo: ${fmt(monto - pagadoActual)})`);
    if (!valor) return;
    const nuevoPagado = Math.min(pagadoActual + parseFloat(valor), monto);
    const { error } = await supabase.from('impuestos').update({
      pagado: nuevoPagado,
      fecha_pago: hoy,
    }).eq('id', id);
    if (error) { alert('Error: ' + error.message); return; }
    cargarDatos();
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminás este registro?')) return;
    await supabase.from('impuestos').delete().eq('id', id);
    cargarDatos();
  }

  function mostrarExito(msg) {
    setExito(msg);
    setTimeout(() => setExito(''), 3000);
  }

  function totalPendiente() {
    return impuestos.filter(i => getEstado(i) !== 'pagado').reduce((s, i) => s + (i.monto - (i.pagado||0)), 0);
  }

  function totalVencido() {
    return impuestos.filter(i => getEstado(i) === 'vencido').reduce((s, i) => s + (i.monto - (i.pagado||0)), 0);
  }

  const proximos = impuestos.filter(i => {
    const dias = (new Date(i.fecha_vencimiento) - new Date()) / (1000*60*60*24);
    return dias >= 0 && dias <= 30 && getEstado(i) !== 'pagado';
  });

  const lbl = { fontSize:12, color:'#888', fontWeight:'bold', display:'block', marginBottom:4 };
  const inp = { padding:'8px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ minHeight:'100vh', background:'#F2F3F4' }}>

      {/* Header */}
      <div style={{ background:'#1B2631', padding:'16px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ color:'#fff', margin:0, fontSize:20 }}>🧾 Impuestos</h1>
          <p style={{ color:'#aaa', margin:'2px 0 0', fontSize:12 }}>Control de obligaciones fiscales</p>
        </div>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'transparent', border:'1px solid #555', color:'#aaa', padding:'8px 16px', borderRadius:8, cursor:'pointer' }}>
          ← Volver
        </button>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:24 }}>

        {exito && (
          <div style={{ background:'#D5F5E3', border:'1px solid #27AE60', borderRadius:8, padding:12, marginBottom:16, color:'#1E8449', fontWeight:'bold', textAlign:'center' }}>
            ✅ {exito}
          </div>
        )}

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
          {[
            { label:'🧾 Total registros',   valor: impuestos.length,    esPlata:false, color:'#2471A3' },
            { label:'⏳ Deuda pendiente',   valor: fmt(totalPendiente()), esPlata:true, color:'#D4AC0D' },
            { label:'🔴 Deuda vencida',     valor: fmt(totalVencido()),  esPlata:true, color:'#C0392B' },
          ].map((k,i) => (
            <div key={i} style={{ background:'#fff', borderRadius:12, padding:16, textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{k.label}</div>
              <div style={{ fontSize:20, fontWeight:'bold', color:k.color }}>{k.valor}</div>
            </div>
          ))}
        </div>

        {/* Alerta próximos vencimientos */}
        {proximos.length > 0 && (
          <div style={{ background:'#FEF9E7', border:'1px solid #D4AC0D', borderRadius:12, padding:16, marginBottom:20 }}>
            <div style={{ fontWeight:'bold', color:'#D4AC0D', marginBottom:8 }}>
              ⚠️ {proximos.length} vencimiento{proximos.length > 1 ? 's' : ''} en los próximos 30 días
            </div>
            {proximos.map(i => (
              <div key={i.id} style={{ fontSize:13, color:'#555', padding:'4px 0', borderBottom:'1px solid #F9E79F' }}>
                <span style={{ fontWeight:'bold' }}>{i.nombre}</span>
                <span style={{ marginLeft:8, color:'#888' }}>
                  vence {new Date(i.fecha_vencimiento+'T12:00:00').toLocaleDateString('es-AR')}
                </span>
                <span style={{ marginLeft:8, fontWeight:'bold', color:'#C0392B' }}>
                  {fmt(i.monto - (i.pagado||0))}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {[['lista','📋 Ver Impuestos'],['nuevo','➕ Nuevo Impuesto']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding:'10px 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:'bold', fontSize:14,
                background: tab===key ? '#D4AC0D' : '#fff',
                color: tab===key ? '#fff' : '#555',
                boxShadow:'0 2px 6px rgba(0,0,0,0.08)'
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* Lista de impuestos */}
        {tab === 'lista' && (
          <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
            <h2 style={{ margin:'0 0 16px', fontSize:16, color:'#1B2631' }}>📋 Obligaciones Fiscales</h2>
            {loading ? (
              <div style={{ textAlign:'center', padding:30, color:'#888' }}>Cargando...</div>
            ) : impuestos.length === 0 ? (
              <div style={{ textAlign:'center', padding:30, color:'#888' }}>
                No hay impuestos registrados. Agregá uno con "➕ Nuevo Impuesto"
              </div>
            ) : (
              impuestos.map(imp => {
                const estado = getEstado(imp);
                const estilo = ESTADO_STYLE[estado];
                const saldo = imp.monto - (imp.pagado||0);
                const diasVenc = Math.ceil((new Date(imp.fecha_vencimiento) - new Date()) / (1000*60*60*24));
                return (
                  <div key={imp.id} style={{ borderBottom:'1px solid #f5f5f5', padding:'16px 0', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                        <span style={{ background:estilo.bg, color:estilo.color, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:'bold' }}>
                          {estilo.label}
                        </span>
                        {estado === 'pendiente' && diasVenc <= 30 && (
                          <span style={{ background:'#FDEBD0', color:'#E67E22', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:'bold' }}>
                            ⚠️ Vence en {diasVenc} días
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:15, fontWeight:'bold', color:'#1B2631' }}>{imp.nombre}</div>
                      {imp.periodo && <div style={{ fontSize:12, color:'#888' }}>Período: {imp.periodo}</div>}
                      <div style={{ fontSize:12, color:'#888' }}>
                        Vencimiento: {new Date(imp.fecha_vencimiento+'T12:00:00').toLocaleDateString('es-AR')}
                      </div>
                      {imp.fecha_pago && (
                        <div style={{ fontSize:12, color:'#27AE60' }}>
                          Pagado el: {new Date(imp.fecha_pago+'T12:00:00').toLocaleDateString('es-AR')}
                        </div>
                      )}
                      {imp.observaciones && <div style={{ fontSize:11, color:'#aaa', fontStyle:'italic', marginTop:2 }}>{imp.observaciones}</div>}
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:13, color:'#888' }}>Total: {fmt(imp.monto)}</div>
                      <div style={{ fontSize:13, color:'#27AE60' }}>Pagado: {fmt(imp.pagado||0)}</div>
                      <div style={{ fontSize:16, fontWeight:'bold', color: saldo > 0 ? '#C0392B' : '#27AE60' }}>
                        {saldo > 0 ? `Debe: ${fmt(saldo)}` : '✅ Al día'}
                      </div>
                      <div style={{ display:'flex', gap:6, marginTop:6, justifyContent:'flex-end' }}>
                        {saldo > 0 && (
                          <button onClick={() => registrarPago(imp.id, imp.pagado||0, imp.monto)}
                            style={{ padding:'4px 12px', background:'#D4AC0D', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:'bold' }}>
                            💰 Pagar
                          </button>
                        )}
                        <button onClick={() => eliminar(imp.id)}
                          style={{ padding:'4px 12px', background:'#FADBD8', color:'#C0392B', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:'bold' }}>
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Nuevo impuesto */}
        {tab === 'nuevo' && (
          <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
            <h2 style={{ margin:'0 0 20px', fontSize:16, color:'#1B2631' }}>➕ Registrar Impuesto</h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Nombre del impuesto *</label>
                <select value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} style={inp}>
                  <option value="">Seleccioná...</option>
                  {TIPOS_IMPUESTO.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Período (Ej: Julio 2026)</label>
                <input placeholder="Ej: Julio 2026" value={form.periodo} onChange={e => setForm({...form, periodo:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Fecha de vencimiento *</label>
                <input type="date" value={form.fecha_vencimiento} onChange={e => setForm({...form, fecha_vencimiento:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Monto total (ARS) *</label>
                <input type="number" placeholder="0" value={form.monto} onChange={e => setForm({...form, monto:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Ya pagado (ARS)</label>
                <input type="number" placeholder="0" value={form.pagado} onChange={e => setForm({...form, pagado:e.target.value})} style={inp} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Observaciones</label>
                <input placeholder="Opcional..." value={form.observaciones} onChange={e => setForm({...form, observaciones:e.target.value})} style={inp} />
              </div>
            </div>
            <button onClick={guardarImpuesto} disabled={guardando}
              style={{ marginTop:20, width:'100%', padding:14, background: guardando ? '#aaa' : '#D4AC0D', color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
              {guardando ? 'Guardando...' : '💾 Guardar Impuesto'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}